import { useState } from "react";
import { Building2, Palette, Upload, X, Image as ImageIcon, Save, ArrowLeft, Plus, Trash2, Award, Users, Mail, Send, Bell, Eye, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { THEME_PRESETS, uploadFile } from "../studioUtils";
import api, { readError } from "../api";
import { WhatsAppSettings } from "../components/WhatsAppSettings";

const emptyRef = () => ({ name: "", logo_url: "", note: "" });

export default function CompanySettings({ company, setCompany, primaryColor, applyPrimaryColor, saveCompany, backTo, isAdmin }) {
  const [testEmailTo, setTestEmailTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [showPreviews, setShowPreviews] = useState(false);
  const setField = (key, value) => setCompany(c => ({ ...c, [key]: value }));

  const previewSample = {
    ref: "KNX-20260215-9F2A",
    customer_name: "Örnek Firma A.Ş.",
    code: "482913",
    decision: "onayladı",
    link: "https://" + (window.location.host || "sirket.com") + "/quote/public/KNX-20260215-9F2A",
    subject: "Sunucu ve depolama teklifi",
    company_name: company.company_name || "Şirket adı",
    certificate_id: "b8f4c9e12a",
  };

  const renderTemplate = (tpl, fallback) => {
    const text = (tpl && tpl.trim()) ? tpl : fallback;
    return text.replace(/\{(\w+)\}/g, (_, k) => previewSample[k] ?? `{${k}}`);
  };
  const nl2br = (s) => (s || "").split("\n").map((line, i, arr) => (
    <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
  ));

  const sendTestEmail = async () => {
    if (!testEmailTo.trim()) return toast.error("E-posta girin");
    setTestBusy(true);
    try {
      await api.post("/settings/test-email", { to: testEmailTo.trim() });
      toast.success(`Test e-postası ${testEmailTo} adresine gönderildi`);
    } catch (err) {
      toast.error(readError(err, "Gönderim başarısız"));
    } finally { setTestBusy(false); }
  };

  const onLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await uploadFile(file);
      setField("logo_url", result.absoluteUrl);
      toast.success("Logo yüklendi");
    } catch (err) { toast.error(readError(err, "Logo yüklenemedi")); }
  };

  const onSignatureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await uploadFile(file);
      setField("signature_url", result.absoluteUrl);
      toast.success("İmza / kaşe yüklendi");
    } catch (err) { toast.error(readError(err, "Yüklenemedi")); }
  };

  const updateRefs = (listKey, updater) =>
    setCompany(c => ({ ...c, [listKey]: updater(c[listKey] || []) }));

  const addRefItem = (listKey) => updateRefs(listKey, list => [...list, emptyRef()]);
  const removeRefItem = (listKey, i) => updateRefs(listKey, list => list.filter((_, idx) => idx !== i));
  const setRefField = (listKey, i, key, value) =>
    updateRefs(listKey, list => list.map((it, idx) => idx === i ? { ...it, [key]: value } : it));
  const uploadRefLogo = async (listKey, i, file) => {
    if (!file) return;
    try {
      const result = await uploadFile(file);
      setRefField(listKey, i, "logo_url", result.absoluteUrl);
    } catch (err) { toast.error(readError(err, "Logo yüklenemedi")); }
  };

  return (
    <section className="settings-view">
      <div className="settings-intro">
        <div className="settings-icon"><Building2 size={22} /></div>
        <div>
          <p className="eyebrow blue">TEKLİF ÜST BİLGİSİ</p>
          <h2>Şirket bilgileri & tema</h2>
          <p>Logo ve renk seçenekleri anında teklif çıktısına uygulanır.</p>
        </div>
      </div>

      <div className="logo-band">
        <div className="logo-preview" data-testid="logo-preview">
          {company.logo_url ? <img src={company.logo_url} alt="logo" /> :
            <div className="logo-placeholder"><ImageIcon size={30} /><span>Logo yok</span></div>}
        </div>
        <div className="logo-actions">
          <label className="outline-button">
            <Upload size={15} /> {company.logo_url ? "Logoyu değiştir" : "Logo yükle"}
            <input data-testid="logo-upload-input" type="file" accept="image/*" onChange={onLogoUpload} hidden />
          </label>
          {company.logo_url && (
            <button data-testid="logo-remove-button" className="outline-button danger" onClick={() => setField("logo_url", "")}>
              <X size={15} /> Kaldır
            </button>
          )}
          <p className="hint">PNG / JPG · Maks 5 MB · Şeffaf arka plan önerilir</p>
        </div>
      </div>

      <div className="logo-band">
        <div className="logo-preview" data-testid="signature-preview">
          {company.signature_url ? <img src={company.signature_url} alt="imza" /> :
            <div className="logo-placeholder"><ImageIcon size={30} /><span>Kaşe / imza yok</span></div>}
        </div>
        <div className="logo-actions">
          <label className="outline-button">
            <Upload size={15} /> {company.signature_url ? "Kaşeyi değiştir" : "Kaşe / imza yükle"}
            <input data-testid="signature-upload-input" type="file" accept="image/*" onChange={onSignatureUpload} hidden />
          </label>
          {company.signature_url && (
            <button data-testid="signature-remove-button" className="outline-button danger" onClick={() => setField("signature_url", "")}>
              <X size={15} /> Kaldır
            </button>
          )}
          <p className="hint">Kaşe + imza görseli · yazdırma çıktısının altında görünür · şeffaf PNG önerilir</p>
        </div>
      </div>

      <div className="theme-band">
        <div className="theme-intro">
          <Palette size={18} />
          <div>
            <b>Tema rengi</b>
            <p>Ana vurgu rengini kişiselleştirin. Değişiklik anında uygulanır.</p>
          </div>
        </div>
        <div className="theme-swatches">
          {THEME_PRESETS.map(p => (
            <button key={p.value} title={p.name}
              data-testid={`theme-swatch-${p.value.replace('#','')}`}
              onClick={() => applyPrimaryColor(p.value)}
              className={`theme-swatch ${primaryColor === p.value ? "active" : ""}`}
              style={{ background: p.value }} />
          ))}
          <label className="theme-custom">
            <span>Özel</span>
            <input data-testid="theme-color-picker" type="color" value={primaryColor}
              onChange={e => applyPrimaryColor(e.target.value)} />
          </label>
        </div>
      </div>

      <div className="settings-grid">
        <label>Firma adı<input data-testid="company-name-input" value={company.company_name} onChange={e => setField("company_name", e.target.value)} /></label>
        <label>Telefon<input data-testid="company-phone-input" value={company.phone} onChange={e => setField("phone", e.target.value)} /></label>
        <label>E-posta<input data-testid="company-email-input" value={company.email} onChange={e => setField("email", e.target.value)} /></label>
        <label>Web sitesi<input data-testid="company-website-input" value={company.website} onChange={e => setField("website", e.target.value)} /></label>
        <label>Vergi numarası<input data-testid="company-tax-input" value={company.tax_number} onChange={e => setField("tax_number", e.target.value)} /></label>
        <label>Vergi dairesi<input data-testid="company-tax-office-input" value={company.tax_office} onChange={e => setField("tax_office", e.target.value)} placeholder="Ör. Kadıköy V.D." /></label>
        <label>Referans kodu öneki<input data-testid="ref-prefix-input" value={company.ref_prefix}
          onChange={e => setField("ref_prefix", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
          placeholder="KNX" /></label>
        <label>Döviz kur marjı (₺)
          <input data-testid="rate-margin-input" type="number" step="0.01" min="0" max="5"
            value={company.rate_margin ?? 0.4}
            onChange={e => setField("rate_margin", parseFloat(e.target.value) || 0)}
            placeholder="0.4" />
          <small className="hint">Otomatik kura eklenecek marj (ör. 45.5 + 0.4 = 45.9)</small>
        </label>
        <label>Döviz kur yuvarlaması
          <select data-testid="rate-rounding-select" value={company.rate_rounding || "0.1"}
            onChange={e => setField("rate_rounding", e.target.value)}>
            <option value="0.01">1 kuruş (0.01)</option>
            <option value="0.1">10 kuruş (0.1)</option>
            <option value="1">1 lira</option>
            <option value="none">Yuvarlama yok</option>
          </select>
          <small className="hint">Marj eklendikten sonra uygulanacak yuvarlama adımı</small>
        </label>
        <label className="full-field">Adres<textarea data-testid="company-address-input" value={company.address} onChange={e => setField("address", e.target.value)} /></label>
        <label className="full-field">Banka bilgileri<textarea data-testid="company-bank-input" value={company.bank_details} onChange={e => setField("bank_details", e.target.value)} placeholder="Banka adı, IBAN, hesap bilgileri" /></label>
      </div>

      <RefBlock icon={<Users size={18} />} title="Referanslarımız"
        description="Teklif çıktısının 2. sayfasında müşteri güveni için görünür."
        textVal={company.references_text}
        setText={v => setField("references_text", v)}
        items={company.references || []}
        listKey="references"
        addItem={() => addRefItem("references")}
        removeItem={i => removeRefItem("references", i)}
        setItemField={(i, k, v) => setRefField("references", i, k, v)}
        uploadLogo={(i, f) => uploadRefLogo("references", i, f)}
      />
      <RefBlock icon={<Award size={18} />} title="Çözüm Ortaklarımız"
        description="HP, Acronis gibi çözüm ortağı firma logolarını ekleyin."
        textVal={company.partners_text}
        setText={v => setField("partners_text", v)}
        items={company.partners || []}
        listKey="partners"
        addItem={() => addRefItem("partners")}
        removeItem={i => removeRefItem("partners", i)}
        setItemField={(i, k, v) => setRefField("partners", i, k, v)}
        uploadLogo={(i, f) => uploadRefLogo("partners", i, f)}
      />

      <div className="ref-block">
        <div className="ref-block-head">
          <div>
            <Mail size={18} />
            <b>E-posta Servisi</b>
            <p>Kendi sunucunuzda çalıştırıyorsanız <b>Resend</b> API anahtarı veya <b>SMTP</b> sunucu bilgilerini girin. Bu platformda ise <b>Yönetimli</b> seçili kalabilir.</p>
          </div>
          <span className={`managed-chip ${company.email_provider === "managed" ? "" : "custom"}`}>
            {company.email_provider === "managed" ? "MANAGED · READY"
              : company.email_provider === "resend" ? "RESEND"
              : "SMTP"}
          </span>
        </div>

        <div className="settings-grid">
          <label className="full-field">Sağlayıcı
            <select data-testid="email-provider" value={company.email_provider || "managed"}
              onChange={e => setField("email_provider", e.target.value)}>
              <option value="managed">Yönetimli (yalnızca bu platformda çalışır)</option>
              <option value="resend">Resend API (kendi sunucumda)</option>
              <option value="smtp">SMTP Sunucusu (Gmail, cPanel, kendi SMTP'niz)</option>
            </select>
          </label>
          <label>Bildirim e-postası (size gelecek)
            <input data-testid="admin-notification-email" type="email"
              value={company.admin_notification_email || ""}
              onChange={e => setField("admin_notification_email", e.target.value)}
              placeholder="info@sirketiniz.com" />
          </label>
          <label>Onay geldiğinde bildirim al
            <select value={company.notify_on_approval ? "1" : "0"}
              onChange={e => setField("notify_on_approval", e.target.value === "1")}
              data-testid="notify-on-approval">
              <option value="1">Evet (önerilen)</option>
              <option value="0">Hayır</option>
            </select>
          </label>
          <label>E-posta gönderen adı
            <input data-testid="reminder-from-name" value={company.reminder_from_name || ""}
              onChange={e => setField("reminder_from_name", e.target.value)}
              placeholder="CDS Teklif Stüdyosu" />
          </label>
        </div>

        {company.email_provider === "resend" && (
          <div className="provider-block">
            <p className="provider-hint">
              <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer">resend.com</a>'dan
              ücretsiz API anahtarı alın, göndereceğiniz domain'i doğrulayın.
            </p>
            <div className="settings-grid">
              <label>Resend API Key
                <input data-testid="resend-api-key" type="password" autoComplete="new-password"
                  value={company.resend_api_key || ""}
                  onChange={e => setField("resend_api_key", e.target.value)}
                  placeholder="re_..." />
              </label>
              <label>Gönderen e-posta (doğrulanmış)
                <input data-testid="resend-from-email" type="email"
                  value={company.resend_from_email || ""}
                  onChange={e => setField("resend_from_email", e.target.value)}
                  placeholder="teklif@sirketiniz.com" />
              </label>
            </div>
          </div>
        )}

        {company.email_provider === "smtp" && (
          <div className="provider-block">
            <p className="provider-hint">
              Örn. Gmail: smtp.gmail.com · Port 587 · StartTLS · uygulama şifresi kullanın.
              cPanel/Plesk: sunucunuzun mail.<b>domain.com</b> ayarlarını girin.
            </p>
            <div className="settings-grid">
              <label>SMTP Sunucu
                <input data-testid="smtp-host" value={company.smtp_host || ""}
                  onChange={e => setField("smtp_host", e.target.value)}
                  placeholder="smtp.gmail.com" />
              </label>
              <label>Port
                <input data-testid="smtp-port" type="number" value={company.smtp_port || 587}
                  onChange={e => setField("smtp_port", parseInt(e.target.value) || 587)} />
              </label>
              <label>Güvenlik
                <select data-testid="smtp-secure" value={company.smtp_secure || "starttls"}
                  onChange={e => setField("smtp_secure", e.target.value)}>
                  <option value="starttls">StartTLS (587)</option>
                  <option value="ssl">SSL / TLS (465)</option>
                  <option value="none">Yok (25 — önerilmez)</option>
                </select>
              </label>
              <label>Gönderen e-posta
                <input data-testid="smtp-from-email" type="email"
                  value={company.smtp_from_email || ""}
                  onChange={e => setField("smtp_from_email", e.target.value)}
                  placeholder="teklif@sirketiniz.com" />
              </label>
              <label>Kullanıcı adı
                <input data-testid="smtp-username" value={company.smtp_username || ""}
                  onChange={e => setField("smtp_username", e.target.value)}
                  placeholder="genelde tam e-posta adresiniz" />
              </label>
              <label>Şifre
                <input data-testid="smtp-password" type="password" autoComplete="new-password"
                  value={company.smtp_password || ""}
                  onChange={e => setField("smtp_password", e.target.value)}
                  placeholder="uygulama şifresi / e-posta şifresi" />
              </label>
            </div>
          </div>
        )}

        <div className="test-email-block">
          <label className="full-field" style={{ margin: 0 }}>Test e-postası gönder
            <div className="test-email-row">
              <input type="email" value={testEmailTo} onChange={e => setTestEmailTo(e.target.value)}
                placeholder="test@ornek.com" data-testid="test-email-input" />
              <button className="primary-button" onClick={sendTestEmail} disabled={testBusy}
                data-testid="test-email-send">
                <Send size={13} /> {testBusy ? "Gönderiliyor..." : "Gönder"}
              </button>
            </div>
            <small className="hint">Önce ayarları kaydedin, sonra test edin.</small>
          </label>
        </div>
      </div>

      <div className="ref-block">
        <div className="ref-block-head">
          <div>
            <Bell size={18} />
            <b>E-posta Şablonları</b>
            <p>İsteğe bağlı — boş bırakılırsa varsayılan Türkçe şablonlar kullanılır. Kullanabileceğiniz değişkenler: <code>{"{ref}"} {"{customer_name}"} {"{code}"} {"{decision}"} {"{link}"} {"{subject}"} {"{company_name}"} {"{certificate_id}"}</code></p>
          </div>
          <button data-testid="toggle-email-preview" className="outline-button"
            onClick={() => setShowPreviews(v => !v)}>
            <Eye size={14} /> {showPreviews ? "Önizlemeyi gizle" : "Önizle"}
          </button>
        </div>
        <div className="settings-grid">
          <label>Onay kodu · Konu
            <input data-testid="tpl-approval-subject" value={company.email_tpl_approval_subject || ""}
              onChange={e => setField("email_tpl_approval_subject", e.target.value)}
              placeholder="Teklif {ref} · Onay kodu: {code}" />
          </label>
          <label>Hatırlatma · Konu
            <input data-testid="tpl-reminder-subject" value={company.email_tpl_reminder_subject || ""}
              onChange={e => setField("email_tpl_reminder_subject", e.target.value)}
              placeholder="{company_name} · Teklif hatırlatma ({ref})" />
          </label>
          <label className="full-field">Onay kodu · Gövde
            <textarea data-testid="tpl-approval-body" value={company.email_tpl_approval_body || ""}
              onChange={e => setField("email_tpl_approval_body", e.target.value)}
              placeholder="Merhaba {customer_name}, {ref} teklifi için onay kodunuz aşağıdadır." />
          </label>
          <label className="full-field">Hatırlatma · Gövde
            <textarea data-testid="tpl-reminder-body" value={company.email_tpl_reminder_body || ""}
              onChange={e => setField("email_tpl_reminder_body", e.target.value)}
              placeholder="Sayın {customer_name}, {ref} numaralı teklifi görüntülemek için: {link}" />
          </label>
          <label>Bildirim (size) · Konu
            <input data-testid="tpl-notify-subject" value={company.email_tpl_notify_subject || ""}
              onChange={e => setField("email_tpl_notify_subject", e.target.value)}
              placeholder="{ref} · Müşteri kararı: {decision}" />
          </label>
          <label className="full-field">Bildirim · Gövde
            <textarea data-testid="tpl-notify-body" value={company.email_tpl_notify_body || ""}
              onChange={e => setField("email_tpl_notify_body", e.target.value)}
              placeholder="{customer_name} teklifi {decision}. Sertifika: {certificate_id}" />
          </label>
        </div>
        {showPreviews && (
          <div className="email-preview-grid" data-testid="email-preview-grid">
            <div className="email-preview-card">
              <div className="epc-head"><b>Onay Kodu</b><span>Müşteriye giden 6 haneli kod e-postası</span></div>
              <div className="epc-subject"><span>Konu:</span> {renderTemplate(company.email_tpl_approval_subject, "Teklif {ref} · Onay kodu: {code}")}</div>
              <div className="epc-body">{nl2br(renderTemplate(company.email_tpl_approval_body, "Sayın {customer_name},\n\n{ref} numaralı teklifi onaylamak veya reddetmek için tek kullanımlık kodunuz: {code}\n\nBu kodu teklif linkinde girip kararınızı bildirebilirsiniz.\n\n{company_name}"))}</div>
            </div>
            <div className="email-preview-card">
              <div className="epc-head"><b>Hatırlatma</b><span>Bekleyen teklifler için müşteriye hatırlatma</span></div>
              <div className="epc-subject"><span>Konu:</span> {renderTemplate(company.email_tpl_reminder_subject, "{company_name} · Teklif hatırlatma ({ref})")}</div>
              <div className="epc-body">{nl2br(renderTemplate(company.email_tpl_reminder_body, "Sayın {customer_name},\n\n{ref} numaralı teklifi görüntülemek ve karar bildirmek için: {link}\n\nSaygılarımızla,\n{company_name}"))}</div>
            </div>
            <div className="email-preview-card">
              <div className="epc-head"><b>Yönetici Bildirimi</b><span>Müşteri karar verince size gelen e-posta</span></div>
              <div className="epc-subject"><span>Konu:</span> {renderTemplate(company.email_tpl_notify_subject, "{ref} · Müşteri kararı: {decision}")}</div>
              <div className="epc-body">{nl2br(renderTemplate(company.email_tpl_notify_body, "{customer_name} teklifi {decision}. Sertifika: {certificate_id}\n\nDetay: {link}"))}</div>
            </div>
          </div>
        )}
      </div>

      <div className="ref-block" data-testid="payment-block">
        <div className="ref-block-head">
          <div>
            <CreditCard size={18} />
            <b>Ödeme Sağlayıcı</b>
            <p>Onaylanan tekliflere ödeme linki oluşturmak için kullanılır. Sandbox anahtarları ile test edin, canlı ortama geçmeden önce doğrulayın.</p>
          </div>
          <span className={`managed-chip ${company.payment_provider && company.payment_provider !== "none" ? "custom" : ""}`}>
            {company.payment_provider === "iyzico" ? "IYZICO" :
             company.payment_provider === "paytr" ? "PAYTR" :
             company.payment_provider === "odeal" ? "ÖDEAL" :
             company.payment_provider === "manual" ? "MANUEL LİNK" : "KAPALI"}
          </span>
        </div>
        <div className="settings-grid">
          <label>Sağlayıcı
            <select data-testid="payment-provider" value={company.payment_provider || "none"}
              onChange={e => setField("payment_provider", e.target.value)}>
              <option value="none">Kapalı</option>
              <option value="iyzico">iyzico</option>
              <option value="paytr">PayTR</option>
              <option value="odeal">Ödeal</option>
              <option value="manual">Manuel link şablonu</option>
            </select>
          </label>
          {company.payment_provider === "manual" && (
            <label className="full-field">Manuel ödeme URL şablonu
              <input data-testid="payment-manual-url" value={company.payment_manual_url || ""}
                onChange={e => setField("payment_manual_url", e.target.value)}
                placeholder="https://banka.com/tahsilat?ref={ref}&amount={amount}" />
              <small className="hint">Değişkenler: {"{ref}"}, {"{amount}"}, {"{customer}"}</small>
            </label>
          )}
        </div>
        {company.payment_provider === "iyzico" && (
          <div className="settings-grid" style={{ marginTop: 12 }}>
            <label>iyzico ortam
              <select data-testid="iyzico-env" value={company.iyzico_env || "sandbox"}
                onChange={e => setField("iyzico_env", e.target.value)}>
                <option value="sandbox">Sandbox</option>
                <option value="live">Canlı</option>
              </select>
            </label>
            <label>&nbsp;</label>
            <label className="full-field">API Key
              <input data-testid="iyzico-api-key" type="password" autoComplete="new-password"
                value={company.iyzico_api_key || ""}
                onChange={e => setField("iyzico_api_key", e.target.value)}
                placeholder="sandbox-... veya production key" />
            </label>
            <label className="full-field">Secret Key
              <input data-testid="iyzico-secret-key" type="password" autoComplete="new-password"
                value={company.iyzico_secret_key || ""}
                onChange={e => setField("iyzico_secret_key", e.target.value)}
                placeholder="sandbox-... veya production secret" />
            </label>
          </div>
        )}
        {company.payment_provider === "paytr" && (
          <div className="settings-grid" style={{ marginTop: 12 }}>
            <label>Merchant ID
              <input data-testid="paytr-merchant-id" value={company.paytr_merchant_id || ""}
                onChange={e => setField("paytr_merchant_id", e.target.value)}
                placeholder="123456" />
            </label>
            <label>Test modu
              <select data-testid="paytr-test-mode" value={company.paytr_test_mode ? "1" : "0"}
                onChange={e => setField("paytr_test_mode", e.target.value === "1")}>
                <option value="1">Açık (test)</option>
                <option value="0">Kapalı (canlı)</option>
              </select>
            </label>
            <label className="full-field">Merchant Key
              <input data-testid="paytr-merchant-key" type="password" autoComplete="new-password"
                value={company.paytr_merchant_key || ""}
                onChange={e => setField("paytr_merchant_key", e.target.value)} />
            </label>
            <label className="full-field">Merchant Salt
              <input data-testid="paytr-merchant-salt" type="password" autoComplete="new-password"
                value={company.paytr_merchant_salt || ""}
                onChange={e => setField("paytr_merchant_salt", e.target.value)} />
            </label>
          </div>
        )}
        {company.payment_provider === "odeal" && (
          <div className="settings-grid" style={{ marginTop: 12 }}>
            <label>Ödeal ortam
              <select data-testid="odeal-env" value={company.odeal_env || "stage"}
                onChange={e => setField("odeal_env", e.target.value)}>
                <option value="stage">Stage / Test</option>
                <option value="live">Canlı</option>
              </select>
            </label>
            <label>&nbsp;</label>
            <label className="full-field">Client ID
              <input data-testid="odeal-client-id" value={company.odeal_client_id || ""}
                onChange={e => setField("odeal_client_id", e.target.value)} />
            </label>
            <label className="full-field">Client Secret
              <input data-testid="odeal-client-secret" type="password" autoComplete="new-password"
                value={company.odeal_client_secret || ""}
                onChange={e => setField("odeal_client_secret", e.target.value)} />
            </label>
          </div>
        )}
      </div>

      {isAdmin && <div className="ref-block">
        <div className="ref-block-head">
          <div><Send size={18} /><b>Verimor SMS</b>
            <p>Müşteri SMS ile doğrulama seçtiğinde kodu bu hesap üzerinden gönderir. Verimor panelinde sunucunun genel IP adresini API izinli IP listesine ekleyin.</p>
          </div>
        </div>
        <div className="settings-grid">
          <label>Verimor kullanıcı adı
            <input value={company.verimor_username || ""} onChange={e => setField("verimor_username", e.target.value)} placeholder="908502551086" />
          </label>
          <label>Onaylı SMS başlığı
            <input value={company.verimor_source_addr || ""} onChange={e => setField("verimor_source_addr", e.target.value)} placeholder="08502551086" />
          </label>
          <label>API şifresi
            <input type="password" autoComplete="new-password" value={company.verimor_api_password || ""}
              onChange={e => setField("verimor_api_password", e.target.value)} placeholder="Verimor API şifresi" />
            <small className="hint">Kaydedilen şifre gizlenir. Değiştirmek için yeni şifreyi yazın.</small>
          </label>
        </div>
      </div>}

      {isAdmin && <WhatsAppSettings />}

      <div className="settings-actions">
        <button data-testid="settings-back-button" className="outline-button" onClick={backTo}>
          <ArrowLeft size={16} /> Teklife dön
        </button>
        <button data-testid="save-company-settings-button" className="primary-button" onClick={saveCompany}>
          <Save size={16} /> Bilgileri kaydet
        </button>
      </div>
    </section>
  );
}

function RefBlock({ icon, title, description, textVal, setText, items, listKey, addItem, removeItem, setItemField, uploadLogo }) {
  return (
    <div className="ref-block">
      <div className="ref-block-head">
        <div>{icon}<b>{title}</b><p>{description}</p></div>
        <button data-testid={`add-${listKey}-button`} className="outline-button" onClick={addItem}>
          <Plus size={14} /> Ekle
        </button>
      </div>
      <label className="full-field">
        Açıklama metni (opsiyonel)
        <textarea value={textVal || ""} onChange={e => setText(e.target.value)}
          data-testid={`${listKey}-text`}
          placeholder="Kısa açıklama / tanıtım metni" />
      </label>
      <div className="ref-grid">
        {items.map((it, i) => (
          <div className="ref-card" key={i} data-testid={`${listKey}-item-${i}`}>
            <button className="ref-remove" onClick={() => removeItem(i)} title="Kaldır"
              data-testid={`${listKey}-remove-${i}`}><X size={13} /></button>
            <label className="ref-logo">
              {it.logo_url ? <img src={it.logo_url} alt="" /> : <ImageIcon size={22} />}
              <input type="file" accept="image/*" hidden
                onChange={e => uploadLogo(i, e.target.files?.[0])} />
            </label>
            <input placeholder="Firma / kurum adı" value={it.name}
              onChange={e => setItemField(i, "name", e.target.value)}
              data-testid={`${listKey}-name-${i}`} />
            <input placeholder="Not (opsiyonel)" value={it.note}
              onChange={e => setItemField(i, "note", e.target.value)} />
          </div>
        ))}
        {items.length === 0 && <div className="ref-empty">Henüz eklenmedi</div>}
      </div>
    </div>
  );
}
